import { FormattedMessage } from 'react-intl';
import Image from 'next/image';
import { centsToDollarString } from 'utils/price';
import { OrderSummaryProps } from './types';

export const OrderSummary = ({
  orderNumber,
  paymentMethod,
  priceDetails: {
    lineItemsTotalPrice,
    cartTotal,
    fractionDigits,
    cartDiscount,
  },
  coursesDetails,
  isFreeOrder,
  localCurrencyCharge,
}: OrderSummaryProps) => {
  const lineItemsPriceInDollars = centsToDollarString(
    lineItemsTotalPrice,
    fractionDigits,
  );
  const discountAmountInDollars = centsToDollarString(
    cartDiscount?.amount,
    fractionDigits,
  );
  const totalAmountInDollars = centsToDollarString(cartTotal, fractionDigits);

  return (
    <section className="col-12 col-md-8 order-summary p-0">
      <div className="row">
        <div className="col-md-6">
          <h2 className="float-md-left tw-text-[32px]">
            <FormattedMessage
              id="payment.thankyou.order.summary"
              defaultMessage="Order summary"
              description="Order summary heading"
            />
          </h2>
        </div>
        <div className="col-md-6">
          <p className="float-md-right text-info-300 mb-0">
            <FormattedMessage
              id="payment.thankyou.order.number"
              defaultMessage="Order #{orderNumber}"
              description="Order number label"
              values={{ orderNumber }}
            />
          </p>
        </div>
      </div>
      {coursesDetails.map(({ name, sku, price, partner, image }) => (
        <div key={sku} className="row item-row mt-2 px-3 ">
          {image && (
            <div>
              <Image
                src={image}
                alt="Course image"
                width={100}
                height={100}
                className="thank-you-product-thumbnail"
              />
            </div>
          )}
          <div className="item-row course-info">
            <div className="course-info">
              {partner && <span className="m-0">{partner}</span>}
              <div className="h4">{name}</div>
            </div>
            <div className="h4 align-self-end" translate="no">
              {price}
            </div>
          </div>
        </div>
      ))}
      <div className="row mt-4">
        <div className="col-6">
          <span>
            <FormattedMessage
              id="payment.thankyou.subtotal"
              defaultMessage="Subtotal"
              description="Subtotal label"
            />
          </span>
        </div>
        <div className="col-6">
          <span className="float-right" translate="no">
            {lineItemsPriceInDollars}
          </span>
        </div>
      </div>
      {discountAmountInDollars !== '$0.00' && (
        <div className="row mt-2">
          <div className="col-6">
            <span>
              <FormattedMessage
                id="payment.thankyou.discount"
                defaultMessage="Discount"
                description="Discount label"
              />
            </span>
          </div>
          <div className="col-6">
            <span className="float-right" translate="no">
              -{discountAmountInDollars}
            </span>
          </div>
        </div>
      )}
      <div className="row">
        <div className="col">
          <hr className="tw-my-4" />
        </div>
      </div>
      <div className="row">
        <div className="col-6 align-self-center">
          <h4 className="m-0">
            <FormattedMessage
              id="payment.thankyou.total"
              defaultMessage="Total"
              description="Total label"
            />
          </h4>
        </div>
        <div className="col-6 align-self-center">
          <h4 className="float-right m-0" translate="no">
            {totalAmountInDollars}
          </h4>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <hr className="tw-my-4" />
        </div>
      </div>
      {!isFreeOrder && (
        <>
          <div className="row">
            <div className="col-6">
              <h4>
                <FormattedMessage
                  id="payment.thankyou.amount.paid"
                  defaultMessage="Amount paid"
                  description="Amount paid label"
                />
              </h4>
            </div>
            <div className="col-6">
              <h4 className="float-right" translate="no">
                {localCurrencyCharge
                  ? localCurrencyCharge.formattedAmount
                  : totalAmountInDollars}
              </h4>
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <FormattedMessage
                id="payment.thankyou.payment.method"
                defaultMessage="Payment method"
                description="Payment method label"
              />
            </div>
            <div className="col-6">
              <p className="float-right text-capitalize">{paymentMethod}</p>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
